import React, { useEffect, useState } from "react";
import { EsBadge } from "../components/EsBadge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert, Plus, Trash2, Pencil, MapPin, User, PenLine, Download, Check, Eye, FileText, ChevronDown } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { ExportButton } from "../components/ExportButton";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import OspiteCombobox from "../components/OspiteCombobox";
import { MeseGiorniEsplosi, RecapOperatoriMese, AllertaBox } from "../components/MeseGiorniEsplosi";
import { SignaturePad } from "../components/SignaturePad";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { PdfBlobViewer } from "../components/PdfBlobViewer";
import { toast } from "sonner";

const ESITI = [
  ["nessuna", "Nessuna conseguenza"],
  ["contusione", "Contusione / escoriazione"],
  ["ferita", "Ferita"],
  ["frattura", "Sospetta frattura"],
  ["trauma_cranico", "Trauma cranico"],
  ["ospedale", "Trasporto in ospedale"],
  ["altro", "Altro"],
];
const esitoLabel = (k) => (ESITI.find((e) => e[0] === k) || [k, k])[1];
const esitoColor = (k) => k === "nessuna" ? "bg-emerald-100 text-emerald-700" : k === "contusione" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

const GRAVITA = [
  ["lieve", "Lieve", "bg-emerald-100 text-emerald-700 border-emerald-300"],
  ["media", "Media", "bg-amber-100 text-amber-700 border-amber-300"],
  ["grave", "Grave", "bg-red-100 text-red-700 border-red-300"],
];
const gravitaInfo = (k) => GRAVITA.find((g) => g[0] === k) || ["lieve", "Lieve", "bg-emerald-100 text-emerald-700 border-emerald-300"];

const fmtGiornoBreve = (iso) => { try { const s = new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); return s.charAt(0).toUpperCase() + s.slice(1); } catch { return iso; } };
const fmtGiornoCompatto = (iso) => { try { const d = new Date(iso + "T00:00:00"); const wd = d.toLocaleDateString("it-IT", { weekday: "long" }); const month = d.toLocaleDateString("it-IT", { month: "long" }); return { wd: wd.charAt(0).toUpperCase() + wd.slice(1), day: d.getDate(), month }; } catch { return null; } };
const isDimesso = (o) => !!(o.data_dimissione && String(o.data_dimissione).trim());

export default function Cadute() {
  const { selectedStructure, structures } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(null);
  const [det, setDet] = useState(null);
  const [operatori, setOperatori] = useState([]);
  const [firmaDlg, setFirmaDlg] = useState(null);
  const [sigData, setSigData] = useState(null);
  const [showDimessi, setShowDimessi] = useState(false);
  const [viewer, setViewer] = useState(null);

  const params = { structure_id: selectedStructure };
  const { data: cadute = [] } = useQuery({ queryKey: ["cadute", selectedStructure], queryFn: async () => (await api.get("/cadute", { params })).data });
  const { data: ospiti = [] } = useQuery({ queryKey: ["ospiti", selectedStructure], queryFn: async () => (await api.get("/ospiti", { params })).data });
  useEffect(() => { api.get("/firma/operatori", { params: { structure_id: dlg?.structure_id || selectedStructure } }).then((r) => setOperatori(r.data)).catch(() => setOperatori([])); }, [selectedStructure, dlg?.structure_id]);

  const structName = (id) => structures.find((s) => s.id === id)?.nome || "";
  const allStruct = selectedStructure === "all";
  const list = [...cadute].sort((a, b) => `${b.data || ""}${b.ora || ""}`.localeCompare(`${a.data || ""}${a.ora || ""}`));
  const meseStr = new Date().toISOString().slice(0, 7);
  const cadMese = list.filter((c) => (c.data || "").startsWith(meseStr));
  const cadOpCount = {};
  cadMese.forEach((c) => { const n = c.operatore || "—"; cadOpCount[n] = (cadOpCount[n] || 0) + 1; });
  const cadOperatori = Object.entries(cadOpCount).map(([nome, count]) => ({ nome, count })).sort((a, b) => b.count - a.count);
  const sette = (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); })();
  const recenti = list.filter((c) => (c.data || "") >= sette);
  const today = new Date().toISOString().slice(0, 10);
  const [openDay, setOpenDay] = useState(today);
  const [mese, setMese] = useState(meseStr);
  const selDate = openDay || today;
  const cadCounts = {};
  list.forEach((c) => { if (c.data) cadCounts[c.data] = (cadCounts[c.data] || 0) + 1; });

  const cadByOspite = {};
  list.forEach((c) => { if (c.ospite_id) (cadByOspite[c.ospite_id] = cadByOspite[c.ospite_id] || []).push(c); });
  const rosterAll = [...ospiti].sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`));
  const attivi = rosterAll.filter((o) => !isDimesso(o));
  const dimessi = rosterAll.filter(isDimesso);

  const cadColor = (structures || []).find((s) => s.id === selectedStructure)?.colore || "#dc2626";
  const cadName = selectedStructure === "all" ? "Tutte le strutture" : ((structures || []).find((s) => s.id === selectedStructure)?.nome || "");
  const cadCols = [
    { header: "Data", accessor: (c) => (c.data ? fmtDate(c.data) : "") },
    { header: "Ora", accessor: "ora" },
    { header: "Ospite", accessor: (c) => c.ospite_nome || "" },
    { header: "Gravità", accessor: (c) => gravitaInfo(c.gravita)[1] },
    { header: "Esito", accessor: (c) => esitoLabel(c.esito) },
    { header: "Operatore", accessor: "operatore" },
    { header: "Luogo", accessor: "luogo" },
    { header: "Descrizione", accessor: "descrizione" },
  ];

  const defStruct = () => selectedStructure !== "all" ? selectedStructure : ((structures || []).length === 1 ? structures[0].id : undefined);
  const openNew = () => setDlg({ data: selDate, ora: new Date().toTimeString().slice(0, 5), esito: "nessuna", gravita: "lieve", structure_id: defStruct(), operatori_coinvolti: [], testimone: "" });
  const openNewFor = (o) => setDlg({ data: selDate, ora: new Date().toTimeString().slice(0, 5), esito: "nessuna", gravita: "lieve", structure_id: o.structure_id || defStruct(), ospite_id: o.id, ospite_nome: `${o.nome} ${o.cognome}`.trim(), operatori_coinvolti: [], testimone: "" });
  const toggleOp = (o) => setDlg((p) => {
    const cur = p.operatori_coinvolti || [];
    const ex = cur.some((x) => x.id === o.id);
    const next = ex ? cur.filter((x) => x.id !== o.id) : [...cur, { id: o.id, nome: `${o.nome} ${o.cognome}`.trim(), qualifica: o.qualifica || o.ruolo || "" }];
    return { ...p, operatori_coinvolti: next };
  });
  const save = async () => {
    const c = dlg;
    if (!c.structure_id) { toast.error("Seleziona una struttura specifica in alto."); return; }
    if (!c.ospite_id && !c.ospite_nome) { toast.error("Seleziona l'ospite coinvolto."); return; }
    if (!(c.operatori_coinvolti || []).length) { toast.error("Seleziona almeno un operatore in turno."); return; }
    if (!c.descrizione || !c.descrizione.trim()) { toast.error("Descrivi come è avvenuta la caduta."); return; }
    const o = ospiti.find((x) => x.id === c.ospite_id);
    const body = { ...c, gravita: c.gravita || "lieve", ospite_nome: o ? `${o.nome} ${o.cognome}` : c.ospite_nome, operatore: (c.operatori_coinvolti || []).map((x) => x.nome).join(", ") };
    try {
      if (c.id) await api.put(`/cadute/${c.id}`, body); else await api.post("/cadute", body);
      qc.invalidateQueries({ queryKey: ["cadute"] });
      setDlg(null);
      toast.success("Caduta registrata — ora fai firmare gli operatori coinvolti");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel salvataggio"); }
  };
  const del = async (id) => { try { await api.delete(`/cadute/${id}`); qc.invalidateQueries({ queryKey: ["cadute"] }); setDet(null); } catch { toast.error("Errore"); } };
  const firmaStato = (c) => {
    const coinvolti = c.operatori_coinvolti || [];
    const firmati = new Set((c.firme || []).map((f) => f.operatore_id));
    return { coinvolti, firmati, tutte: coinvolti.length > 0 && coinvolti.every((o) => firmati.has(o.id)) };
  };
  const saveFirma = async () => {
    if (!sigData) { toast.error("Firma prima di salvare"); return; }
    const { caduta, operatore } = firmaDlg;
    const firme = [...(caduta.firme || []).filter((f) => f.operatore_id !== operatore.id),
      { operatore_id: operatore.id, nome: operatore.nome, qualifica: operatore.qualifica || "", firma_img: sigData, timestamp: new Date().toISOString() }];
    try {
      await api.put(`/cadute/${caduta.id}`, { ...caduta, firme });
      qc.invalidateQueries({ queryKey: ["cadute"] });
      setFirmaDlg(null); setSigData(null);
      setDet((d) => d && d.id === caduta.id ? { ...caduta, firme } : d);
      toast.success(`Firma di ${operatore.nome} registrata`);
    } catch { toast.error("Errore nel salvare la firma"); }
  };
  const scaricaPdf = async (c) => {
    try {
      const res = await api.get(`/cadute/${c.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `attestazione_caduta_${(c.ospite_nome || "ospite").replace(/ /g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.status === 400 ? "Devono firmare tutti gli operatori coinvolti" : "Errore nel download"); }
  };
  const anteprimaPdf = async (c) => {
    try {
      const res = await api.get(`/cadute/${c.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setViewer({ url, blob, name: `attestazione_caduta_${(c.ospite_nome || "ospite").replace(/ /g, "_")}.pdf` });
    } catch (e) { toast.error(e.response?.status === 400 ? "Devono firmare tutti gli operatori coinvolti" : "Errore nell'anteprima"); }
  };
  const scaricaViewer = () => {
    if (!viewer) return;
    const a = document.createElement("a");
    a.href = viewer.url; a.download = viewer.name;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const renderRow = (o, num) => {
    const cad = cadByOspite[o.id] || [];
    const last = cad[0];
    const g = last ? gravitaInfo(last.gravita) : null;
    const lastCompatto = last ? fmtGiornoCompatto(last.data) : null;
    const dim = isDimesso(o);
    return (
      <div key={o.id} className={`gr-card p-3 ${dim ? "opacity-80" : ""}`} data-testid={`caduta-row-${o.id}`}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold tabnum">{num != null ? num : "–"}</span>
            <div className="min-w-0">
              <div className="font-semibold flex items-center gap-2 flex-wrap">
                <button type="button" onClick={() => navigate(`/ospiti/${o.id || o.ospite_id}?tab=cadute`)} className="hover:underline text-left" data-testid={`cadute-nome-${o.id}`}>{o.cognome} {o.nome}</button><EsBadge id={o.id || o.ospite_id} nome={`${o.cognome} ${o.nome}`} />
                {o.camera && <span className="text-xs text-muted-foreground font-normal">· cam. {o.camera}</span>}
                {dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">Dimesso</span>}
                {allStruct && o.structure_id && <span className="text-[11px] text-muted-foreground">· {structName(o.structure_id)}</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {cad.length === 0 ? "Nessuna caduta registrata" : `${cad.length} ${cad.length === 1 ? "caduta registrata" : "cadute registrate"}`}
              </div>
            </div>
          </div>

          <div className="lg:w-64 shrink-0">
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1 flex items-center gap-1"><FileText size={11} /> Documenti</div>
            {cad.length === 0 ? <span className="text-xs text-muted-foreground">—</span> : (
              <div className="flex flex-wrap gap-1.5" data-testid={`caduta-docs-${o.id}`}>
                {cad.map((c) => {
                  const st = firmaStato(c);
                  return (
                    <div key={c.id} className="inline-flex items-center gap-1 rounded-full border bg-white pl-2 pr-1 py-0.5 text-[11px]">
                      <button onClick={() => setDet(c)} className="font-semibold hover:underline" title="Dettaglio caduta" data-testid={`caduta-doc-${c.id}`}>{c.data ? fmtDate(c.data) : "—"}</button>
                      {st.tutte ? (
                        <>
                          <button onClick={() => anteprimaPdf(c)} title="Anteprima documento" className="p-1 rounded hover:bg-secondary" data-testid={`caduta-doc-view-${c.id}`}><Eye size={13} /></button>
                          <button onClick={() => scaricaPdf(c)} title="Scarica documento" className="p-1 rounded hover:bg-secondary" data-testid={`caduta-doc-dl-${c.id}`}><Download size={13} /></button>
                        </>
                      ) : (
                        <button onClick={() => setDet(c)} className="inline-flex items-center gap-0.5 text-amber-700 font-semibold px-1" title="Documento da firmare" data-testid={`caduta-doc-sign-${c.id}`}><PenLine size={11} /> da firmare</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="lg:w-24 shrink-0">
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Ultima caduta</div>
            {last && lastCompatto ? (
              <div className="leading-tight" data-testid={`caduta-ultima-${o.id}`}>
                <div className="text-xs font-semibold text-foreground">{lastCompatto.wd} {lastCompatto.day}</div>
                <div className="text-[11px] text-muted-foreground">{lastCompatto.month}</div>
              </div>
            ) : <span className="text-xs text-muted-foreground">—</span>}
          </div>

          <div className="lg:w-28 shrink-0">
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Esito</div>
            {last ? (
              <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${esitoColor(last.esito)}`} data-testid={`caduta-esito-cell-${o.id}`}>{esitoLabel(last.esito)}</span>
            ) : <span className="text-xs text-muted-foreground">—</span>}
          </div>

          <div className="lg:w-20 shrink-0">
            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Gravità</div>
            {last && g ? (
              <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full border ${g[2]}`} data-testid={`caduta-gravita-badge-${o.id}`}>{g[1]}</span>
            ) : <span className="text-xs text-muted-foreground">—</span>}
          </div>

          <Button onClick={() => openNewFor(o)} className="font-semibold shrink-0 w-full lg:w-auto" data-testid={`caduta-nuova-${o.id}`}><Plus size={16} className="mr-1" /> Nuova caduta</Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <MeseGiorniEsplosi
        title="Cadute e infortuni" subtitle="Elenco ospiti · registra una caduta e consulta i documenti firmati"
        Icon={TriangleAlert} color={cadColor}
        mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
        counts={cadCounts} countLabel="cadute"
        monthTop={<>
          <AllertaBox tone="bad" title="Cadute recenti (ultimi 7 giorni)" icon={TriangleAlert}
            items={recenti} testid="cadute-allerta-box" onItemClick={(it) => it.ospite_id && navigate(`/ospiti/${it.ospite_id}?tab=cadute`)}
            renderItem={(it) => (<span><b>{it.ospite_nome}</b> <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${gravitaInfo(it.gravita)[2]}`}>{gravitaInfo(it.gravita)[1]}</span> <span className="ml-1 text-red-700 font-semibold">{esitoLabel(it.esito)}</span> <span className="text-muted-foreground">· {it.data ? fmtDate(it.data) : ""} {it.ora || ""}</span></span>)} />
          <RecapOperatoriMese operatori={cadOperatori} color={cadColor} label="cadute" icon={TriangleAlert} />
        </>}
        detailRight={<>
          <ExportButton filename="cadute" title="Cadute e infortuni" subtitle={cadName} columns={cadCols} rows={list} color={cadColor} testid="cadute-export" />
          <Button onClick={openNew} data-testid="new-caduta" className="font-semibold"><Plus size={18} className="mr-1" /> Nuova caduta</Button>
        </>}
      >
        {rosterAll.length === 0 ? (
          <div className="gr-card p-10 text-center text-sm text-muted-foreground" data-testid="cadute-roster-empty">Nessun ospite in questa struttura.</div>
        ) : (
          <div className="space-y-2" data-testid="cadute-roster">
            {attivi.map((o, i) => renderRow(o, i + 1))}
            {dimessi.length > 0 && (
              <div data-testid="cadute-dimessi-section">
                <button onClick={() => setShowDimessi((s) => !s)} className="w-full flex items-center gap-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 mt-2 text-sm font-bold text-orange-800" data-testid="cadute-dimessi-toggle">
                  <ChevronDown size={15} className={`transition-transform ${showDimessi ? "" : "-rotate-90"}`} /> Dimessi ({dimessi.length})
                </button>
                {showDimessi && <div className="space-y-2 mt-2">{dimessi.map((o) => renderRow(o, null))}</div>}
              </div>
            )}
          </div>
        )}
      </MeseGiorniEsplosi>

      {/* Dettaglio caduta */}
      <Dialog open={!!det} onOpenChange={(o) => !o && setDet(null)}>
        <DialogContent className="max-w-xl">
          {det && (() => {
            const st = firmaStato(det);
            const g = gravitaInfo(det.gravita);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 flex-wrap">Caduta di {det.ospite_nome}
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${g[2]}`}>{g[1]}</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${esitoColor(det.esito)}`}>{esitoLabel(det.esito)}</span>
                  </DialogTitle>
                  <DialogDescription>{det.data ? fmtGiornoBreve(det.data) : ""} {det.ora || ""}{det.luogo ? ` · ${det.luogo}` : ""}</DialogDescription>
                </DialogHeader>
                {det.descrizione && <p className="text-sm whitespace-pre-wrap bg-secondary/40 rounded-lg p-3">{det.descrizione}</p>}
                {det.testimone && <p className="text-xs text-muted-foreground">Testimone: <b className="text-foreground">{det.testimone}</b></p>}
                <div className="mt-1">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Firme operatori</div>
                  <div className="flex flex-wrap items-center gap-2" data-testid={`caduta-firme-${det.id}`}>
                    {st.coinvolti.map((o) => {
                      const done = st.firmati.has(o.id);
                      return (
                        <button key={o.id} type="button" onClick={() => { setSigData(null); setFirmaDlg({ caduta: det, operatore: o }); }}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${done ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"}`}
                          data-testid={`caduta-firma-op-${det.id}-${o.id}`}>
                          {done ? <Check size={13} /> : <PenLine size={13} />} {o.nome} {done ? "· firmato" : "· firma"}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <DialogFooter className="flex-wrap gap-2 sm:justify-between">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setDlg({ ...det }); setDet(null); }} data-testid={`caduta-edit-${det.id}`}><Pencil size={14} className="mr-1" /> Modifica</Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => del(det.id)} data-testid={`caduta-del-${det.id}`}><Trash2 size={14} className="mr-1" /> Elimina</Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={!st.tutte} onClick={() => anteprimaPdf(det)} data-testid={`caduta-view-${det.id}`}><Eye size={14} className="mr-1" /> Anteprima</Button>
                    <Button size="sm" disabled={!st.tutte} onClick={() => scaricaPdf(det)} data-testid={`caduta-pdf-${det.id}`} title={st.tutte ? "Scarica PDF" : "Disponibile quando tutti hanno firmato"}><Download size={14} className="mr-1" /> PDF</Button>
                  </div>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Form caduta */}
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{dlg?.id ? "Modifica caduta" : "Nuova caduta / infortunio"}</DialogTitle>
            <DialogDescription>Registra chi è caduto, l'operatore in turno e cosa è successo esattamente.</DialogDescription>
          </DialogHeader>
          {dlg && (() => { const g = gravitaInfo(dlg.gravita); return (
            <div className={`rounded-lg border px-3 py-2 text-sm font-bold flex items-center gap-2 ${g[2]}`} data-testid="caduta-gravita-banner">
              <TriangleAlert size={16} /> Gravità: {g[1]}
            </div>
          ); })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Ospite coinvolto *</Label>
              <OspiteCombobox value={dlg?.ospite_id || ""} onChange={(o) => setDlg((p) => ({ ...p, ospite_id: o.id }))} ospiti={ospiti} testid="caduta-ospite" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Gravità della caduta *</Label>
              <div className="flex gap-2" data-testid="caduta-gravita">
                {GRAVITA.map(([k, lbl, cls]) => (
                  <button type="button" key={k} onClick={() => setDlg((p) => ({ ...p, gravita: k }))}
                    className={`flex-1 h-10 rounded-lg text-sm font-bold border transition-colors ${dlg?.gravita === k ? cls : "bg-white hover:bg-secondary"}`}
                    data-testid={`caduta-gravita-${k}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Operatori in turno coinvolti *</Label>
              <div className="flex flex-wrap gap-2 p-2 rounded-lg border bg-secondary/30 max-h-40 overflow-auto" data-testid="caduta-operatori">
                {operatori.length === 0 && <span className="text-xs text-muted-foreground">Nessun operatore disponibile</span>}
                {operatori.map((o) => {
                  const sel = (dlg?.operatori_coinvolti || []).some((x) => x.id === o.id);
                  return (
                    <button type="button" key={o.id} onClick={() => toggleOp(o)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold border transition-colors ${sel ? "bg-primary text-white border-primary" : "bg-white hover:bg-secondary"}`}
                      data-testid={`caduta-op-${o.id}`}>
                      {sel && <Check size={13} />} {o.nome} {o.cognome}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Testimone (persona esterna, facoltativo)</Label>
              <Input value={dlg?.testimone || ""} onChange={(e) => setDlg((p) => ({ ...p, testimone: e.target.value }))} placeholder="Nome del testimone non tra gli operatori" data-testid="caduta-testimone" />
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Esito</Label>
              <Select value={dlg?.esito || "nessuna"} onValueChange={(v) => setDlg((p) => ({ ...p, esito: v }))}>
                <SelectTrigger data-testid="caduta-esito"><SelectValue /></SelectTrigger>
                <SelectContent>{ESITI.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:contents">
              <div><Label className="text-xs font-semibold mb-1 block">Data</Label><Input type="date" value={dlg?.data || ""} onChange={(e) => setDlg((p) => ({ ...p, data: e.target.value }))} data-testid="caduta-data" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Ora</Label><Input type="time" value={dlg?.ora || ""} onChange={(e) => setDlg((p) => ({ ...p, ora: e.target.value }))} data-testid="caduta-ora" /></div>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs font-semibold mb-1 block">Luogo</Label><Input value={dlg?.luogo || ""} onChange={(e) => setDlg((p) => ({ ...p, luogo: e.target.value }))} placeholder="es. bagno, camera, corridoio..." data-testid="caduta-luogo" /></div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Descrizione — cosa è successo esattamente *</Label>
              <Textarea rows={5} value={dlg?.descrizione || ""} onChange={(e) => setDlg((p) => ({ ...p, descrizione: e.target.value }))} placeholder="Descrivi la dinamica: come e dove è caduto, se si è fatto male, cosa è stato fatto..." data-testid="caduta-descrizione" />
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-background border-t gap-2">
            <Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button>
            <Button onClick={save} data-testid="caduta-save" className="font-semibold">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Firma */}
      <Dialog open={!!firmaDlg} onOpenChange={(o) => { if (!o) { setFirmaDlg(null); setSigData(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Firma di {firmaDlg?.operatore?.nome}</DialogTitle>
            <DialogDescription>Firma con il dito (su tablet) o con il mouse per attestare la caduta di <b>{firmaDlg?.caduta?.ospite_nome}</b>.</DialogDescription>
          </DialogHeader>
          {firmaDlg && <SignaturePad onChange={setSigData} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFirmaDlg(null); setSigData(null); }}>Annulla</Button>
            <Button onClick={saveFirma} disabled={!sigData} data-testid="caduta-firma-save" className="font-semibold">Salva firma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anteprima documento in-app (evita blocco pop-up su mobile/tablet) */}
      <Dialog open={!!viewer} onOpenChange={(o) => { if (!o) { if (viewer) window.URL.revokeObjectURL(viewer.url); setViewer(null); } }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="caduta-viewer-dialog">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="flex items-center gap-2"><FileText size={18} /> Anteprima documento</DialogTitle>
            <DialogDescription className="sr-only">Anteprima del documento PDF della caduta</DialogDescription>
          </DialogHeader>
          {viewer && <PdfBlobViewer blob={viewer.blob} className="max-h-[70vh]" />}
          <DialogFooter className="px-4 pb-4 gap-2">
            <Button variant="outline" size="sm" onClick={scaricaViewer} data-testid="caduta-viewer-download"><Download size={15} className="mr-1" /> Scarica</Button>
            <Button size="sm" onClick={() => { if (viewer) window.URL.revokeObjectURL(viewer.url); setViewer(null); }} data-testid="caduta-viewer-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
